import type { LibraryTemplate } from "@/common/types/templates";
import { LibraryItem } from "./library-item";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useTemplateLibraryStore } from "@/store/templateLibrary.store";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { getTemplateLanguage } from "@/utils/languageMapping";
import i18n from "@/common/i18n";

export const LibraryList = ({
  libraryTemplates = [],
  useLazyLoading = false,
}: {
  libraryTemplates?: LibraryTemplate[];
  useLazyLoading?: boolean;
}): JSX.Element => {
  const {
    categories,
    categoriesLoading,
    categoryTemplates,
    categoryLoading,
    fetchCategories,
    fetchTemplatesByCategory,
    clearLanguageCache,
  } = useTemplateLibraryStore();

  // Group templates by category (for non-lazy loading mode)
  const groupedTemplates = libraryTemplates.reduce(
    (accumulator, template) => {
      const category = template.category || "Uncategorized";
      accumulator[category] ??= [];
      accumulator[category].push(template);
      return accumulator;
    },
    {} as Record<string, LibraryTemplate[]>
  );

  // Track which categories are expanded
  // In non-lazy mode, expand all by default; in lazy mode, start collapsed
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    useLazyLoading ? new Set() : new Set(Object.keys(groupedTemplates))
  );

  // Track previous language to detect changes
  const previousLanguageRef = useRef(i18n.language);
  const categoriesLoadedRef = useRef<string | null>(null);
  // Track which categories have been requested to avoid duplicate requests
  const requestedCategoriesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (useLazyLoading && previousLanguageRef.current !== i18n.language) {
      clearLanguageCache(previousLanguageRef.current);
      previousLanguageRef.current = i18n.language;
      categoriesLoadedRef.current = null;
      requestedCategoriesRef.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLazyLoading, i18n.language]);

  // Load categories on mount and when language changes if using lazy loading
  useEffect(() => {
    if (useLazyLoading && !categoriesLoading) {
      const currentLanguage = i18n.language;

      if (categoriesLoadedRef.current !== currentLanguage) {
        categoriesLoadedRef.current = currentLanguage;
        void fetchCategories(currentLanguage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLazyLoading, i18n.language, categoriesLoading]);
  const expandedCategoriesStringRef = useRef<string>("");

  useEffect(() => {
    if (!useLazyLoading) return;

    const mappedLanguage = i18n.language
      ? getTemplateLanguage(i18n.language)
      : undefined;
    const languageKey = mappedLanguage || "all";

    // Create a stable string representation of expanded categories
    const expandedCategoriesArray = Array.from(expandedCategories).sort(
      (a, b) => a.localeCompare(b)
    );
    const expandedCategoriesString = expandedCategoriesArray.join(",");

    // Only proceed if expanded categories actually changed
    if (expandedCategoriesString === expandedCategoriesStringRef.current) {
      return;
    }
    expandedCategoriesStringRef.current = expandedCategoriesString;

    // Get latest state from store to avoid stale closures
    const store = useTemplateLibraryStore.getState();
    const currentCategoryTemplates = store.categoryTemplates;
    const currentCategoryLoading = store.categoryLoading;

    // Clean up requested categories for categories that are no longer expanded
    const currentRequestKeys = new Set<string>();
    for (const category of expandedCategoriesArray) {
      const categoryKey = `${category}-${languageKey}`;
      currentRequestKeys.add(categoryKey);

      // Only fetch if not already loaded, not loading, and not already requested
      if (
        !currentCategoryTemplates[categoryKey] &&
        !currentCategoryLoading[categoryKey] &&
        !requestedCategoriesRef.current.has(categoryKey)
      ) {
        requestedCategoriesRef.current.add(categoryKey);
        void fetchTemplatesByCategory(category, i18n.language).finally(() => {
          requestedCategoriesRef.current.delete(categoryKey);
        });
      }
    }

    // Clean up requested set for categories that are no longer expanded
    for (const key of requestedCategoriesRef.current) {
      if (!currentRequestKeys.has(key)) {
        requestedCategoriesRef.current.delete(key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLazyLoading, expandedCategories, i18n.language]);

  const toggleCategory = (category: string): void => {
    setExpandedCategories((previous) => {
      const next = new Set(previous);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const categoriesList = useLazyLoading
    ? categories
    : Object.keys(groupedTemplates);

  // Sort categories alphabetically, but put "Uncategorized" at the end
  const sortedCategories = [...categoriesList].sort((a, b) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    return a.localeCompare(b);
  });

  if (useLazyLoading && categoriesLoading) {
    return (
      <div className="flex justify-center mt-20">
        <LoadingSpinner size={8} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sortedCategories.map((category) => {
        const mappedLanguage = i18n.language
          ? getTemplateLanguage(i18n.language)
          : undefined;
        const languageKey = mappedLanguage || "all";
        const categoryKey = `${category}-${languageKey}`;
        const templates = useLazyLoading
          ? categoryTemplates[categoryKey] || []
          : groupedTemplates[category] || [];
        const isLoading = useLazyLoading
          ? categoryLoading[categoryKey] || false
          : false;
        const isExpanded = expandedCategories.has(category);

        return (
          <Collapsible
            key={category}
            open={isExpanded}
            onOpenChange={() => {
              toggleCategory(category);
            }}
          >
            <div className="border border-grey-200 rounded-lg overflow-hidden">
              <CollapsibleTrigger className="flex w-full items-center justify-between px-6 py-4 bg-grey-50 hover:bg-grey-100 transition-colors">
                <h2 className="text-lg font-semibold text-grey-900">
                  {category}
                </h2>
                <ChevronDown
                  className={cn(
                    "h-5 w-5 text-grey-500 transition-transform duration-200",
                    isExpanded && "transform rotate-180"
                  )}
                />
              </CollapsibleTrigger>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.645, 0.045, 0.355, 1] }}
                animate={
                  isExpanded
                    ? { opacity: 1, height: "auto" }
                    : { opacity: 0, height: 0 }
                }
              >
                <CollapsibleContent>
                  {((): JSX.Element => {
                    if (isLoading) {
                      return (
                        <div className="flex justify-center py-8">
                          <LoadingSpinner size={6} />
                        </div>
                      );
                    }
                    if (templates.length > 0) {
                      return (
                        <div className="divide-y">
                          {templates.map((template) => (
                            <LibraryItem
                              key={template.id}
                              template={template}
                            />
                          ))}
                        </div>
                      );
                    }
                    return (
                      <div className="py-8 text-center text-grey-500">
                        No templates found in this category
                      </div>
                    );
                  })()}
                </CollapsibleContent>
              </motion.div>
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
};
